<template>
  <Modal variant="dashboard" @close="close">
    <template slot="content">
      <div class="p-dashboard-form">
        <div class="p-dashboard-form__header">
          <h2 class="p-dashboard-form__title">
            公開したURLを入力し、タスクを完了しましょう
          </h2>
        </div>
        <div class="p-dashboard-form__content">
          <div class="c-fieldset c-fieldset--wrap">
            <div class="c-fieldset__header">
              <p class="c-fieldset__title">
                ページURLを登録する<span class="c-required">必須</span>
              </p>
            </div>
            <div class="c-fieldset__content">
              <InputUrl :url="project.siteUrl">
                <Input
                  v-model="params.url"
                  placeholder="/"
                  :class="{
                    alert: isEdited.url && error.url
                  }"
                  @focusout="focusoutUrl"
                />
                <Alert v-if="isEdited.url && error.url">
                  {{ error.url }}
                </Alert>
              </InputUrl>
            </div>
          </div>
          <div class="c-fieldset c-fieldset--wrap">
            <div class="c-fieldset__header">
              <p class="c-fieldset__title">施策完了日を変更する</p>
            </div>
            <div class="c-fieldset__content c-alertPos">
              <DatePicker v-model="params.typeDoneDate" />
              <Alert v-if="error.typeDoneDate">
                {{ error.typeDoneDate }}
              </Alert>
            </div>
          </div>
        </div>
        <div class="p-dashboard-form__footer c-alertPos">
          <Button
            class="p-dashboard-form__cancel"
            @click="close"
            target="_blank"
            size="w80"
            fsizse="f16"
            variant="back"
            radius
          >
            戻る
          </Button>
          <Button
            class="p-dashboard-form"
            @click="submit"
            target="_blank"
            size="w180"
            fsizse="f16"
            variant="back"
            radius
            :disabled="isDisabled"
            :class="{
              'is-active': !isDisabled,
              'is-loading': this.isProcessing
            }"
          >
            タスクを完了する
          </Button>
          <Alert v-if="errorResponse">
            {{ errorResponse }}
          </Alert>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import { ERROR_MESSAGE, TASK_LENGTHS_LIMIT } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import DatePicker from '@/components/DatePicker';
import Input from '@/components/Input';
import InputUrl from '@/components/InputUrl';
import Modal from '@/components/Modal';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  props: {
    item: {
      type: Object,
      required: true
    },
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    Alert,
    Button,
    DatePicker,
    Input,
    InputUrl,
    Modal
  },
  data() {
    return {
      error: {
        url: ''
      },
      errorResponse: '',
      params: {
        typeDoneDate: this.item.typeDoneDate
          ? new Date(this.item.typeDoneDate)
          : new Date(),
        url: this.$options.filters.removeDomain(
          this.item.url,
          this.project.siteUrl
        )
      },
      isEdited: {
        url: false,
        typeDoneDate: false
      },
      isProcessing: false
    };
  },
  computed: {
    isDisabled() {
      //バリデーションエラーorAPI処理実行中orURLが空
      if (this.isError || this.isProcessing || !this.params.url) {
        return true;
      }

      return false;
    },
    isError() {
      // 選択した施策内容によってエラー条件が変わるため
      if (this.error.url) {
        return true;
      }

      return false;
    }
  },
  watch: {
    params: {
      handler() {
        // 何か入力したらサーバーからのエラーメッセージを消す
        this.errorResponse = '';
      },
      deep: true
    },
    'params.url': {
      handler(value, oldValue) {
        const url = value.trim();
        this.validateUrl(url);

        if (this.item.url) {
          const oldUrl = this.$options.filters.removeDomain(
            this.item.url,
            this.project.siteUrl
          );
          this.isEdited.url = url !== oldUrl;
        } else if (typeof oldValue !== 'undefined') {
          this.isEdited.url = true;
        }
      },
      immediate: true
    },
    'params.typeDoneDate': {
      handler(value, oldValue) {
        if (this.item.typeDoneDate) {
          this.isEdited.typeDoneDate =
            utils.formatDate(value, 'YYYY-MM-DD') !=
            utils.formatDate(this.item.typeDoneDate, 'YYYY-MM-DD');
        } else {
          this.isEdited.typeDoneDate = !!value;
        }
      },
      immediate: true
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    openModalObservationKeyword(item) {
      this.$emit('openModalRegisterObservationKeyword', item);
    },
    async submit() {
      //URL未入力のままHTMLを直接編集されてボタンを押された場合は処理を終了する
      if (!this.params.url) {
        this.close();
      }

      this.isProcessing = true;
      const params = this.getTaskParams();

      const item = Object.assign({}, this.item);

      await apiProjects.tasks
        .put(params, this.auth.idToken, this.project.id, this.item.id)
        .then(response => {
          item.column = params.column;
          item.url = params.url;
          item.typeDoneDate = params.typeDoneDate;
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
          this.isProcessing = false;
          return;
        });

      //タスク完了時にエラーが発生したら処理を中断する
      if (this.errorResponse) {
        this.isProcessing = false;
        return;
      }

      const keywordSearchParams = {
        keyword: this.item.keyword,
        url: params.url
      };

      const keywordId = await apiProjects.observationKeywords.search
        .get(keywordSearchParams, this.auth.idToken, this.project.id)
        .then(response => response.data.id)
        .catch(error => null);

      // 環境変数のisDemoはstring型
      const isDemo = `${this.$constants.isDemo}` === 'true';

      // フォームに入力されたURLでタスクを更新
      this.item.url = params.url;

      if (!keywordId || isDemo) {
        //検索順位チェック用のモーダルを開く処理 更新後のオブジェクトを渡す
        this.openModalObservationKeyword(this.item);
      }

      this.isProcessing = false;
      //タスク完了後のタスクの配列から削除
      this.$emit('completeTask', item);
      this.close();
      return;
    },
    focusoutUrl() {
      //入力後にtrimする
      this.params.url = this.$options.filters.normalizeAllSpaces(
        this.params.url
      );
      // 先頭が/じゃないURLが入力されていたら、スラッシュを付ける
      if (this.params.url && !/^\//.test(this.params.url)) {
        this.params.url = '/' + this.params.url;
      }
    },
    validateUrl(url) {
      //URLは必須入力項目
      this.error.url = new Validation().input().test(url);
    },
    getFullUrl(url) {
      if (!url) {
        return url;
      }
      return (
        this.$options.filters.removeTrailingSlash(this.project.siteUrl) +
        '/' +
        this.$options.filters.removeLeadingSlash(url)
      );
    },
    getTaskParams() {
      const params = {
        url: this.getFullUrl(this.params.url),
        typeDoneDate: this.params.typeDoneDate
          ? utils.formatDate(this.params.typeDoneDate, 'YYYY-MM-DD')
          : utils.formatDate(new Date(), 'YYYY-MM-DD'),
        // column,type,position,taskDetailがリクエストに必要なので明示的に挿入
        column: 'done',
        type: 'createContent',
        position: 0,
        keyword: this.item.keyword,
        taskDetail: this.item.taskDetail,
        executionDate: this.item.executionDate,
        operatorUserId: this.item.operatorUserId,
        operatorUserName: this.item.operatorUserName,
        note: this.item.note,
        updatedAt: this.item.updatedAt
      };
      return params;
    }
  }
};
</script>
